import { connect } from 'react-redux';
import { orderBy } from 'lodash';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useState, useEffect } from 'react';

import { openGenericModal, refreshGenericModal } from '@actions/modal';

import { Button, Tooltip } from '@commons/utils/styledLibraryComponents';
import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import Text, { ENUM_COLORS } from '@commons/Text';

import {
  ConversationListContainer,
  ConversationListContent,
  ConversationListHeader,
  ConversationItem,
  ConversationInfo,
  Icon,
  ConversationListGroup,
  ConversationItemsContainer,
  IconsContainer,
  ConversationName,
} from './styledComponents';
import { getParamsModal } from '../../utils/modalConfigurations';

import CreditsProgressBar from '../CreditsProgressBar';

const ConversationList = (props) => {
  const {
    conversations,
    currentConversationId,
    dailyUsage,
    deleteConversation,
    openGenericModal,
    refreshGenericModal,
    setSelectedConversationId,
    startNewConversation,
    updateConversation,
  } = props;

  const [sortedConversations, setSortedConversations] = useState([]);
  const [sortedFavoriteConversations, setSortedFavoriteConversations] = useState([]);

  // Used to update the conversation name in the modal
  const [currentConversation, setCurrentConversation] = useState(null);
  const [updatedConversationName, setUpdatedConversationName] = useState('');

  useEffect(() => {
    const conversationsArray = Object.values(conversations);

    const { favoriteConversations, otherConversations } = conversationsArray.reduce(
      (acc, conversation) => {
        if (conversation.favorite) {
          acc.favoriteConversations.push(conversation);
          return acc;
        }

        acc.otherConversations.push(conversation);

        return acc;
      },
      { favoriteConversations: [], otherConversations: [] },
    );

    setSortedConversations(orderBy(otherConversations, 'updatedAt', 'desc'));
    setSortedFavoriteConversations(orderBy(favoriteConversations, 'updatedAt', 'desc'));
  }, [conversations]);

  useEffect(() => {
    refreshGenericModal(
      getParamsModal({
        inputValue: updatedConversationName,
        closeCleanUp: () => setUpdatedConversationName(''),
        errorMessage: '',
        setValue: setUpdatedConversationName,
        handleSave: () =>
          updateConversation({ ...currentConversation, name: updatedConversationName }),
      }),
    );
  }, [updatedConversationName]);

  const handleUpdateConversationName = (conversation) => {
    setCurrentConversation(conversation);
    setUpdatedConversationName(conversation.name);

    openGenericModal(
      getParamsModal({
        inputValue: updatedConversationName,
        closeCleanUp: () => setUpdatedConversationName(''),
        errorMessage: '',
        setValue: setUpdatedConversationName,
        handleSave: () => updateConversation({ ...conversation, name: updatedConversationName }),
      }),
    );
  };

  const handleStartNewConversation = () => {
    if (dailyUsage === 100) {
      return;
    }
    startNewConversation();
  };

  return (
    <ConversationListContainer>
      <ConversationListHeader>
        <Tooltip
          renderTooltipTrigger={() => (
            <Button
              color={'inpulse-default'}
              handleClick={handleStartNewConversation}
              icon={'/images/inpulse/add-white-small.svg'}
              isDisabled={dailyUsage === 100}
              label={i18next.t('AGENT.AGENT.NEW_CHAT')}
            />
          )}
          text={dailyUsage === 100 ? i18next.t('AGENT.AGENT.CREDITS_LIMIT_REACHED') : ''}
        />
      </ConversationListHeader>

      <ConversationListContent>
        {sortedFavoriteConversations.length > 0 && (
          <ConversationListGroup>
            <Text color={ENUM_COLORS.DGREY}>{i18next.t('AGENT.AGENT.FAVORITES')}</Text>
            <ConversationItemsContainer>
              {sortedFavoriteConversations.map((conversation) => (
                <ConversationItem
                  isActive={conversation.id === currentConversationId}
                  key={conversation.id}
                  onClick={() => setSelectedConversationId(conversation.id)}
                >
                  <ConversationInfo>
                    <ConversationName>
                      <Text
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {conversation.name || i18next.t('AGENT.AGENT.NEW_CHAT')}
                      </Text>
                      {!conversation.isNewConversation && (
                        <Icon
                          src={'/images/inpulse/pen-black-small.svg'}
                          style={{ width: '10px', height: '10px' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpdateConversationName(conversation);
                          }}
                          onMouseOut={(e) => (e.target.src = '/images/inpulse/pen-black-small.svg')}
                          onMouseOver={(e) =>
                            (e.target.src = '/images/inpulse/pen-dmgrey-small.svg')
                          }
                        />
                      )}
                    </ConversationName>
                    <Text color={ENUM_COLORS.DGREY}>
                      {(conversation.updatedAt || moment()).format(
                        DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR,
                      )}
                    </Text>
                  </ConversationInfo>

                  {!conversation.isNewConversation && (
                    <Icon
                      src={'/images/inpulse/favorite-black-small.svg'}
                      onClick={(e) => {
                        e.stopPropagation();
                        updateConversation({
                          ...conversation,
                          favorite: !conversation.favorite,
                        });
                      }}
                      onMouseOut={(e) =>
                        (e.target.src = '/images/inpulse/favorite-black-small.svg')
                      }
                      onMouseOver={(e) =>
                        (e.target.src = '/images/inpulse/favorite-dmgrey-small.svg')
                      }
                    />
                  )}
                </ConversationItem>
              ))}
            </ConversationItemsContainer>
          </ConversationListGroup>
        )}

        {sortedConversations.length > 0 && (
          <ConversationListGroup>
            <Text color={ENUM_COLORS.DGREY}>{i18next.t('AGENT.AGENT.HISTORY')}</Text>
            <ConversationItemsContainer>
              {sortedConversations.map((conversation) => (
                <ConversationItem
                  isActive={conversation.id === currentConversationId}
                  key={conversation.id}
                  onClick={() => setSelectedConversationId(conversation.id)}
                >
                  <ConversationInfo>
                    <ConversationName>
                      <Text
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {conversation.name || i18next.t('AGENT.AGENT.NEW_CHAT')}
                      </Text>
                      {!conversation.isNewConversation && (
                        <Icon
                          src={'/images/inpulse/pen-black-small.svg'}
                          style={{ width: '10px', height: '10px' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpdateConversationName(conversation);
                          }}
                          onMouseOut={(e) => (e.target.src = '/images/inpulse/pen-black-small.svg')}
                          onMouseOver={(e) =>
                            (e.target.src = '/images/inpulse/pen-dmgrey-small.svg')
                          }
                        />
                      )}
                    </ConversationName>
                    <Text color={ENUM_COLORS.DGREY}>
                      {(conversation.updatedAt || moment()).format(
                        DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR,
                      )}
                    </Text>
                  </ConversationInfo>

                  <IconsContainer>
                    {!conversation.isNewConversation && (
                      <Icon
                        src={'/images/inpulse/favorite-dmgrey-small.svg'}
                        onClick={(e) => {
                          e.stopPropagation();
                          updateConversation({
                            ...conversation,
                            favorite: !conversation.favorite,
                          });
                        }}
                        onMouseOut={(e) =>
                          (e.target.src = '/images/inpulse/favorite-dmgrey-small.svg')
                        }
                        onMouseOver={(e) =>
                          (e.target.src = '/images/inpulse/favorite-black-small.svg')
                        }
                      />
                    )}
                    {sortedConversations.length + sortedFavoriteConversations.length > 1 && (
                      <Icon
                        src={'/images/inpulse/trash-black-small.svg'}
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteConversation(conversation.id);
                        }}
                        onMouseOut={(e) => (e.target.src = '/images/inpulse/trash-black.svg')}
                        onMouseOver={(e) => (e.target.src = '/images/inpulse/trash-red.svg')}
                      />
                    )}
                  </IconsContainer>
                </ConversationItem>
              ))}
            </ConversationItemsContainer>
          </ConversationListGroup>
        )}
      </ConversationListContent>
      <CreditsProgressBar dailyUsage={dailyUsage} />
    </ConversationListContainer>
  );
};

const mapDispatchToProps = (dispatch) => ({
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  refreshGenericModal: (params) => {
    dispatch(refreshGenericModal(params));
  },
});

export default connect(null, mapDispatchToProps)(ConversationList);
